import React from 'react';

import { css } from '@emotion/css';
import { P21LeadTimeSource, P21SafetyStockType } from '@recurrency/core-api-schema/dist/common/enums';
import { theme } from 'theme';

import { FlexSpace } from 'components/FlexSpace';
import { BorderedPropertyItem } from 'components/recipes/equation/BorderedPropertyItem';
import { Typography } from 'components/Typography';

import { formatNumber, formatPercent, formatUSD } from 'utils/formatting';
import { showTransferBasedSpokeMinMaxes } from 'utils/tenantSettings';

import { formatSafetyStockToStr, safetyStockSourceNames } from '../utils';

export interface EOQFormulaMaxEquationData {
  annual_demand?: number;
  lead_time_demand_qty?: number;
  avg_demand?: number;
  lead_time: number;
  lead_time_source?: P21LeadTimeSource;
  is_spoke?: boolean;
  unit_of_measure: string;
  recommended_min: number;
  recommended_max: number;
  safety_stock_qty?: number;
  safety_stock_value: number;
  safety_stock_type: P21SafetyStockType;
  eoq?: number;
  order_cost?: number;
  holding_cost?: number;
  unit_cost?: string;
  raw_recommended_max: number;
}

export const EOQFormulaMaxEquation = ({ record }: { record: EOQFormulaMaxEquationData }) => {
  const yearlyDemandValue = record.annual_demand ?? 0;
  const showTransferBasedMinMaxes = showTransferBasedSpokeMinMaxes() && record.is_spoke;
  const maxMonthsText = showTransferBasedMinMaxes ? '1 month' : '6 months';
  const maxDemandValue = showTransferBasedMinMaxes ? yearlyDemandValue / 12.0 : yearlyDemandValue / 2.0;

  return (
    <FlexSpace
      direction="column"
      align-items="start"
      className={css`
        align-content: flex-start;
      `}
    >
      <FlexSpace>
        <BorderedPropertyItem
          label="Forecast Over Lead Time"
          aria-disabled={record.recommended_min === record.recommended_max}
          value={`${formatNumber(record.lead_time_demand_qty ?? 0)} ${record.unit_of_measure}`}
          description={
            <Typography type="small" style={{ color: theme.colors.primary[500] }}>
              {`Lead Time Demand: ${formatNumber(record.avg_demand)} ${record.unit_of_measure} / day`}
              <br />
              {`Lead Time: ${formatNumber(record.lead_time)} days`}
              <br />
              {`Source: ${record.lead_time_source === P21LeadTimeSource.override ? 'Override' : 'Smart'}`}
            </Typography>
          }
        />
        <div
          className={css`
            align-self: stretch;
            padding-top: 16px;
          `}
        >
          <Typography type="large">+</Typography>
        </div>
        <BorderedPropertyItem
          label="Safety Stock Qty"
          aria-disabled={record.recommended_min === record.recommended_max}
          value={`${formatNumber(record.safety_stock_qty ?? 0)} ${record.unit_of_measure}`}
          description={
            <Typography type="small" style={{ color: theme.colors.primary[500] }}>
              {`Safety Stock Demand: ${formatNumber(record.avg_demand)} ${record.unit_of_measure} / day`}
              <br />
              {`Safety Stock: ${formatSafetyStockToStr(record.safety_stock_value, record.safety_stock_type)}`}
              <br />
              {`Source: ${safetyStockSourceNames[record.safety_stock_type]}`}
            </Typography>
          }
        />
        <div
          className={css`
            align-self: stretch;
            padding-top: 16px;
          `}
        >
          <Typography type="large">+</Typography>
        </div>
        <BorderedPropertyItem
          label="Economic Order Qty"
          aria-disabled={record.recommended_min === record.recommended_max}
          value={`${formatNumber(record.eoq ?? 0)}  ${record.unit_of_measure}`}
          description={
            <Typography type="small" style={{ color: theme.colors.primary[500] }}>
              {`Yearly Forecast: ${formatNumber(yearlyDemandValue)} ${record.unit_of_measure}`}
              <br />
              {`Replenishment Cost: ${formatUSD(record.order_cost, true, 2)} / `}{' '}
              <a
                href="https://help.recurrency.com/en/articles/11418965-economic-order-quantity-eoq#01J88S1SBT6N04254BK9X97S8A"
                target="_blank"
                rel="noopener noreferrer"
              >
                purchase order line
              </a>
              <br />
              {`Holding Cost: ${formatPercent(record.holding_cost)} / year`}
              <br />
              {`Unit Cost: ${formatUSD(record.unit_cost ?? 0, true, 2)}`}
              <br />
              Max EOQ: {formatNumber(maxDemandValue)} {record.unit_of_measure} ({maxMonthsText} of demand)
            </Typography>
          }
        />
        <div
          className={css`
            align-self: stretch;
            padding-top: 16px;
          `}
        >
          <Typography type="large">=</Typography>
        </div>
        <BorderedPropertyItem
          label="Recommended Max"
          value={`${formatNumber(record.raw_recommended_max)} ${record.unit_of_measure}`}
        />
      </FlexSpace>
    </FlexSpace>
  );
};
